import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

// T137 — bidirectional URL <-> filter state. The URL query string is the single
// source of truth so a filtered view is shareable, bookmarkable, and Back-able.
//
// `spec` maps each filter KEY to { param, kind: 'text' | 'set' | 'enum', default }.
// We parse on read, serialize on write, and OMIT defaults/empties so shared links
// stay clean.
//
// setValue(patch, { replace }) — pass replace:true for high-frequency writes
// (search keystrokes) and the default-view redirect so they don't spam the Back
// button; discrete edits (toggles, segments) use the default replace:false and
// step through history.

// T351 — a set value may contain the delimiter.
//
// Sets are carried in the URL as one comma-joined param, which is fine while
// every value is a source slug or a country code. T328 routed `company` through
// the same codec, and company is raw scraped text: 34 names in this corpus
// carry a comma, all of them Danish legal forms — "Nordea Danmark, filial af
// Nordea Bank Abp", "PwC, Statsautoriseret Revisionspartnerselskab". Clicking
// that chip wrote `co=Nordea Danmark, filial af Nordea Bank Abp` and read back
// two values, neither of which matches a row: the filter selects nothing and
// the chip never shows as pressed, so it cannot even be un-clicked.
//
// Encoding each value before the join keeps the param one field per value and
// costs nothing for the slugs, which contain no reserved characters. Decoding
// is tolerant of the un-encoded form so URLs already saved as views keep
// working.
const joinSet = (v) => [...(v ?? [])].map((x) => encodeURIComponent(String(x))).sort().join(',');
const splitSet = (raw) => new Set(
  (raw ? String(raw).split(',') : [])
    .filter(Boolean)
    .map((x) => { try { return decodeURIComponent(x); } catch { return x; } }),
);

export function useUrlFilters(spec) {
  const [params, setParams] = useSearchParams();

  const value = useMemo(() => {
    const out = {};
    for (const [k, def] of Object.entries(spec)) {
      const raw = params.get(def.param);
      if (def.kind === 'set') out[k] = splitSet(raw);
      else out[k] = raw ?? def.default ?? '';
    }
    return out;
    // T108 — `spec` is a module-level constant at every call site, so this dep
    // is free today. Listed anyway: a caller that ever passes an inline object
    // should see the memo thrash rather than silently serve a stale spec.
  }, [params, spec]);

  const setValue = useCallback(
    (patch, options = {}) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        const def = spec[k];
        if (!def) continue;
        const s = def.kind === 'set' ? joinSet(v) : v ?? '';
        if (!s || s === def.default) next.delete(def.param);
        else next.set(def.param, s);
      }
      setParams(next, { replace: options.replace ?? false });
    },
    [params, setParams, spec],
  );

  return [value, setValue];
}

// Canonical param-keyed serialization of a filter value (Sets → sorted-comma
// strings; defaults/empties → ''), so a saved view round-trips through the URL
// identically and two states compare equal iff their canonical maps match.
export function serializeFilters(spec, value) {
  const out = {};
  for (const [k, def] of Object.entries(spec)) {
    const v = value[k];
    const s = def.kind === 'set' ? joinSet(v) : v ?? def.default ?? '';
    out[def.param] = s === (def.default ?? '') ? '' : s;
  }
  return out;
}

// Turn a saved-view query (canonical param-map) back into a setValue patch
// (Sets reconstructed, defaults restored for absent params).
export function parseQuery(spec, query) {
  const patch = {};
  for (const [k, def] of Object.entries(spec)) {
    const raw = (query && query[def.param]) || '';
    patch[k] = def.kind === 'set' ? splitSet(raw) : raw || def.default || '';
  }
  return patch;
}

// Stable equality of two canonical param-maps (used for activeViewId).
export function sameQuery(a, b) {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}

// Pure Set toggle (no mutation) — returns a new Set with `val` flipped.
export function toggle(set, val) {
  const next = new Set(set);
  if (next.has(val)) next.delete(val);
  else next.add(val);
  return next;
}
